import { STATUS_KEYS } from "@features/vessel-call/vessel-call-status/PortServices/preview/statusKeys";
import { approveByMDDebunkeringSchema } from "../schemas/approveByMDDebunkeringSchema";
import { approveByOperationsDebunkeringSchema } from "../schemas/approveByOperationsDebunkeringSchema";
import { closeDebunkeringSchema } from "../schemas/closeDebunkeringSchema";
import { rejectionSchema } from "../schemas/rejectionSchema";
import { startDebunkeringSchema } from "../schemas/startDebunkeringSchema";
import type { PortServiceFlow } from "../waste-disposal/utils";

const MARINE_DEPARTMENT_NOTIFY_ROLES = ["Marine Operator"];

export const DEBUNKERING_FLOW: PortServiceFlow = {
  [STATUS_KEYS.PENDING_APPROVAL]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["MD"],
    canReject: ["MD"],
    nextStatus: STATUS_KEYS.MD_APPROVED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: ["Shipping Agent"],
    defaultNotifyRolesByStatus: {
      [STATUS_KEYS.REJECTED]: ["Shipping Agent"],
    },
    shouldAppendNotifyRoles: true,
    statusesList: {
      [STATUS_KEYS.MD_APPROVED]: {
        label: "Approved by Marine Department",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canApprovePortService",
        extraFields: ["externalNotes", "internalNotes"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortService",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.MD_APPROVED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Shipping Agent"],
    canReject: [],
    nextStatus: STATUS_KEYS.STARTED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: [],
    defaultNotifyRolesByStatus: {
      [STATUS_KEYS.REJECTED]: ["Shipping Agent"],
    },
    shouldAppendNotifyRoles: true,
    statusesList: {
      [STATUS_KEYS.STARTED]: {
        label: "Started",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canStartPortService",
        extraFields: ["ats", "etc", "ets"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortService",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.STARTED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Shipping Agent"],
    canReject: [],
    nextStatus: STATUS_KEYS.CLOSED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: MARINE_DEPARTMENT_NOTIFY_ROLES,
    canCancel: [
      {
        userRoles: ["marine-operator"],
        permission: "canCancelPortService",
      },
    ],
    statusesList: {
      [STATUS_KEYS.CLOSED]: {
        label: "Closed",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canClosePortService",
        extraFields: ["atc", "actualQuantity", "serviceReportAttachment"],
      },
    },
  },
};

export const DEBUNKERING_WITH_OPERATIONS_FLOW: PortServiceFlow = {
  [STATUS_KEYS.PENDING_APPROVAL]: DEBUNKERING_FLOW[STATUS_KEYS.PENDING_APPROVAL],
  [STATUS_KEYS.MD_APPROVED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Operations"],
    canReject: ["Operations"],
    nextStatus: STATUS_KEYS.OPERATIONS_APPROVED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: ["Shipping Agent"],
    defaultNotifyRolesByStatus: {
      [STATUS_KEYS.OPERATIONS_APPROVED]: ["Shipping Agent"],
      [STATUS_KEYS.REJECTED]: ["Shipping Agent"],
    },
    shouldAppendNotifyRoles: true,
    statusesList: {
      [STATUS_KEYS.OPERATIONS_APPROVED]: {
        label: "Approved by Operations",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canApprovePortServicesDebunkering",
        extraFields: ["notes", "attachment"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortServicesDebunkering",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.OPERATIONS_APPROVED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Shipping Agent"],
    canReject: [],
    nextStatus: STATUS_KEYS.STARTED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: [],
    defaultNotifyRolesByStatus: {
      [STATUS_KEYS.REJECTED]: ["Shipping Agent"],
    },
    shouldAppendNotifyRoles: true,
    statusesList: {
      [STATUS_KEYS.STARTED]: {
        label: "Started",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canStartPortService",
        extraFields: ["ats", "etc", "ets"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortService",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.STARTED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Shipping Agent"],
    canReject: [],
    nextStatus: STATUS_KEYS.CLOSED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: MARINE_DEPARTMENT_NOTIFY_ROLES,
    canCancel: [
      {
        userRoles: ["operations", "marine-operator"],
        permission: "canCancelPortService",
      },
    ],
    statusesList: {
      [STATUS_KEYS.CLOSED]: {
        label: "Closed",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canClosePortService",
        extraFields: ["atc", "actualQuantity", "serviceReportAttachment"],
      },
    },
  },
};

type DebunkeringDocument = {
  documentUrl: string;
  documentName: string;
  fileType: string;
};

export const validateDebunkeringExtraFields = ({
  newStatusOption,
  notifyOption,
  internalNotes,
  externalNotes,
  rejectionReason,
  notes,
  attachment,
  flowAts,
  flowEtc,
  flowAtc,
  actualQuantity,
  serviceReportAttachment,
}: {
  newStatusOption: string;
  notifyOption: string[];
  internalNotes: string;
  externalNotes: string;
  rejectionReason: string;
  notes: string;
  attachment: DebunkeringDocument;
  flowAts: string | Date | null;
  flowEtc: string | Date | null;
  flowAtc: string | Date | null;
  actualQuantity?: number;
  serviceReportAttachment: DebunkeringDocument;
}) => {
  switch (newStatusOption) {
    case STATUS_KEYS.MD_APPROVED:
      return approveByMDDebunkeringSchema().safeParse({
        internalNotes,
        externalNotes,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.OPERATIONS_APPROVED:
      return approveByOperationsDebunkeringSchema().safeParse({
        notes,
        attachment,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.REJECTED:
      return rejectionSchema().safeParse({
        rejectionReason: rejectionReason || "",
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.STARTED:
      return startDebunkeringSchema().safeParse({
        ats: flowAts,
        etc: flowEtc,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.CLOSED:
      return closeDebunkeringSchema(flowAts).safeParse({
        atc: flowAtc,
        actualQuantity,
        serviceReportAttachment,
        notificationOption: notifyOption,
      });

    default:
      return null;
  }
};
